import { computed } from 'vue'
import type {
  AdminPermissionResource,
  InternalDepartment,
  InternalRoleName,
  PermissionLevel,
} from '@swipe4work/models-orval'
import { useAdminAccessStore } from '@/shared/stores/AdminAccessStore'

/**
 * Ergonomic wrapper around {@link useAdminAccessStore} for reflecting the current internal employee's
 * admin permissions in the UI. Use this in scripts and templates to disable/hide controls the user may
 * not use — menu items, `:disabled` bindings, tab visibility, etc. For plain buttons and links prefer
 * the `v-permission` directive, which additionally shows a lock icon.
 *
 * All checks read reactive store state, so `can(...)` recomputes when access is (re)loaded. Write
 * implies Read and a global admin passes every check — both handled by the store.
 */
export function usePermissions() {
  const access = useAdminAccessStore()

  /** Whether the employee may use the given resource at the given level. */
  const can = (resource: AdminPermissionResource, level: PermissionLevel): boolean =>
    access.hasPermission(resource, level)

  /** Whether the employee holds the given internal role in the given department. */
  const canRole = (name: InternalRoleName, department: InternalDepartment): boolean =>
    access.hasRole(name, department)

  /** Whether the employee has global (super-admin) permissions. */
  const isGlobal = computed(() => access.globalPermissions)

  return {
    can,
    canRole,
    isGlobal,
  }
}
